import { defineChain } from "viem";
import { arbitrum as vab, arbitrumGoerli as vabg, arbitrumSepolia as vabs, base as vbase, baseGoerli as vbaseg, baseSepolia as vbases, bsc as vbsc, bscTestnet as vbsct, fraxtal as vfrax, goerli as vgo, mainnet as vmain, optimism as vop, optimismGoerli as vopg, optimismSepolia as vops, polygon as vpg, polygonAmoy as vpga, polygonMumbai as vpgm, sepolia as vsep, arbitrumNova as vabn, zora as vzora, zoraSepolia as vzoras, } from "viem/chains";
/**
 * Defines an Alchemy chain configuration by adding an Alchemy-specific RPC base URL to the chain's RPC URLs.
 *
 * @example
 * ```ts
 * import { defineAlchemyChain } from "@account-kit/infra";
 * import { sepolia } from "viem/chains";
 *
 * const chain = defineAlchemyChain({
 *  chain: sepolia,
 *  rpcBaseUrl: "https://eth-sepolia.g.alchemy.com/v2"
 * });
 * ```
 *
 * @param {AlchemyChainConfig} params The parameters for defining the Alchemy chain
 * @param {Chain} params.chain The original chain configuration
 * @param {string} params.rpcBaseUrl The Alchemy-specific RPC base URL
 * @returns {Chain} The updated chain configuration with the Alchemy RPC URL added
 */
export const defineAlchemyChain = ({ chain, rpcBaseUrl, }) => {
    return {
        ...chain,
        rpcUrls: {
            ...chain.rpcUrls,
            alchemy: {
                http: [rpcBaseUrl],
            },
        },
    };
};
export const arbitrum = {
    ...vab,
    rpcUrls: {
        ...vab.rpcUrls,
        alchemy: {
            http: ["https://arb-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const arbitrumGoerli = {
    ...vabg,
    rpcUrls: {
        ...vabg.rpcUrls,
        alchemy: {
            http: ["https://arb-goerli.g.alchemy.com/v2"],
        },
    },
};
export const arbitrumSepolia = {
    ...vabs,
    rpcUrls: {
        ...vabs.rpcUrls,
        alchemy: {
            http: ["https://arb-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const goerli = {
    ...vgo,
    rpcUrls: {
        ...vgo.rpcUrls,
        alchemy: {
            http: ["https://eth-goerli.g.alchemy.com/v2"],
        },
    },
};
export const mainnet = {
    ...vmain,
    rpcUrls: {
        ...vmain.rpcUrls,
        alchemy: {
            http: ["https://eth-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const optimism = {
    ...vop,
    rpcUrls: {
        ...vop.rpcUrls,
        alchemy: {
            http: ["https://opt-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const optimismGoerli = {
    ...vopg,
    rpcUrls: {
        ...vopg.rpcUrls,
        alchemy: {
            http: ["https://opt-goerli.g.alchemy.com/v2"],
        },
    },
};
export const optimismSepolia = {
    ...vops,
    rpcUrls: {
        ...vops.rpcUrls,
        alchemy: {
            http: ["https://opt-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const sepolia = {
    ...vsep,
    rpcUrls: {
        ...vsep.rpcUrls,
        alchemy: {
            http: ["https://eth-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const base = {
    ...vbase,
    rpcUrls: {
        ...vbase.rpcUrls,
        alchemy: {
            http: ["https://base-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const baseGoerli = {
    ...vbaseg,
    rpcUrls: {
        ...vbaseg.rpcUrls,
        alchemy: {
            http: ["https://base-goerli.g.alchemy.com/v2"],
        },
    },
};
export const baseSepolia = {
    ...vbases,
    rpcUrls: {
        ...vbases.rpcUrls,
        alchemy: {
            http: ["https://base-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const bsc = {
    ...vbsc,
    rpcUrls: {
        ...vbsc.rpcUrls,
        alchemy: {
            http: ["https://bnb-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const bscTestnet = {
    ...vbsct,
    rpcUrls: {
        ...vbsct.rpcUrls,
        alchemy: {
            http: ["https://bnb-testnet.g.alchemy.com/v2"],
        },
    },
};
export const polygonMumbai = {
    ...vpgm,
    rpcUrls: {
        ...vpgm.rpcUrls,
        alchemy: {
            http: ["https://polygon-mumbai.g.alchemy.com/v2"],
        },
    },
};
export const polygonAmoy = {
    ...vpga,
    rpcUrls: {
        ...vpga.rpcUrls,
        alchemy: {
            http: ["https://polygon-amoy.g.alchemy.com/v2"],
        },
    },
};
export const polygon = {
    ...vpg,
    rpcUrls: {
        ...vpg.rpcUrls,
        alchemy: {
            http: ["https://polygon-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const fraxtal = {
    ...vfrax,
    rpcUrls: {
        ...vfrax.rpcUrls,
    },
};
export const fraxtalSepolia = defineChain({
    id: 2523,
    name: "Fraxtal Sepolia",
    nativeCurrency: { name: "Frax Ether", symbol: "frxETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://rpc.testnet-sepolia.frax.com"],
        },
    },
});
export const zora = {
    ...vzora,
    rpcUrls: {
        ...vzora.rpcUrls,
    },
};
export const zoraSepolia = {
    ...vzoras,
    rpcUrls: {
        ...vzoras.rpcUrls,
    },
};
export const worldChainSepolia = defineChain({
    id: 4801,
    name: "World Chain Sepolia",
    network: "World Chain Sepolia",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://worldchain-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://worldchain-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://worldchain-sepolia.g.alchemy.com/v2"],
        },
    },
});
export const worldChain = defineChain({
    id: 480,
    name: "World Chain",
    network: "World Chain",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://worldchain-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://worldchain-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://worldchain-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const shapeSepolia = defineChain({
    id: 11011,
    name: "Shape Sepolia",
    network: "Shape Sepolia",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://shape-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://shape-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://shape-sepolia.g.alchemy.com/v2"],
        },
    },
});
export const shape = defineChain({
    id: 360,
    name: "Shape",
    network: "Shape",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://shape-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://shape-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://shape-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const unichainMainnet = defineChain({
    id: 130,
    name: "Unichain Mainnet",
    network: "Unichain Mainnet",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://unichain-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://unichain-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://unichain-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const unichainSepolia = defineChain({
    id: 1301,
    name: "Unichain Sepolia",
    network: "Unichain Sepolia",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://unichain-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://unichain-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://unichain-sepolia.g.alchemy.com/v2"],
        },
    },
});
export const soneiumMinato = defineChain({
    id: 1946,
    name: "Soneium Minato",
    network: "Soneium Minato",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://soneium-minato.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://soneium-minato.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://soneium-minato.g.alchemy.com/v2"],
        },
    },
});
export const soneiumMainnet = defineChain({
    id: 1868,
    name: "Soneium Mainnet",
    network: "Soneium Mainnet",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://soneium-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://soneium-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://soneium-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const opbnbTestnet = defineChain({
    id: 5611,
    name: "OPBNB Testnet",
    network: "OPBNB Testnet",
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://opbnb-testnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://opbnb-testnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://opbnb-testnet.g.alchemy.com/v2"],
        },
    },
});
export const opbnbMainnet = defineChain({
    id: 204,
    name: "OPBNB Mainnet",
    network: "OPBNB Mainnet",
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://opbnb-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://opbnb-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://opbnb-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const beraChainBartio = defineChain({
    id: 80084,
    name: "BeraChain Bartio",
    network: "BeraChain Bartio",
    nativeCurrency: { name: "Bera", symbol: "BERA", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://berachain-bartio.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://berachain-bartio.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://berachain-bartio.g.alchemy.com/v2"],
        },
    },
});
export const inkMainnet = defineChain({
    id: 57073,
    name: "Ink Mainnet",
    network: "Ink Mainnet",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://ink-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://ink-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://ink-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const inkSepolia = defineChain({
    id: 763373,
    name: "Ink Sepolia",
    network: "Ink Sepolia",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://ink-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://ink-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://ink-sepolia.g.alchemy.com/v2"],
        },
    },
});
export const arbitrumNova = {
    ...vabn,
    rpcUrls: {
        ...vabn.rpcUrls,
    },
};
export const monadTestnet = defineChain({
    id: 10143,
    name: "Monad Testnet",
    nativeCurrency: { name: "Monad", symbol: "MON", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://monad-testnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://monad-testnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://monad-testnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://testnet.monadexplorer.com",
        },
    },
    testnet: true,
});
export const monadMainnet = defineChain({
    id: 143,
    name: "Monad Mainnet",
    nativeCurrency: { name: "Monad", symbol: "MON", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://monad-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://monad-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://monad-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://mainnet-beta.monvision.io",
        },
    },
    testnet: false,
});
export const mekong = defineChain({
    id: 7078815900,
    name: "Mekong Pectra Devnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://rpc.mekong.ethpandaops.io"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://explorer.mekong.ethpandaops.io",
        },
    },
    testnet: true,
});
export const openlootSepolia = defineChain({
    id: 905905,
    name: "Openloot Sepolia",
    nativeCurrency: { name: "Openloot", symbol: "OL", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://openloot-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://openloot-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://openloot-sepolia.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://openloot-sepolia.explorer.alchemy.com",
        },
    },
    testnet: true,
});
export const gensynTestnet = defineChain({
    id: 685685,
    name: "Gensyn Testnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://gensyn-testnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://gensyn-testnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://gensyn-testnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://gensyn-testnet.explorer.alchemy.com",
        },
    },
    testnet: true,
});
export const riseTestnet = defineChain({
    id: 11155931,
    name: "Rise Testnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://rise-testnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://rise-testnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://rise-testnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://explorer.testnet.riselabs.xyz",
        },
    },
    testnet: true,
});
export const storyMainnet = defineChain({
    id: 1514,
    name: "Story Mainnet",
    nativeCurrency: { name: "IP", symbol: "IP", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://story-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://story-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://story-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://www.storyscan.io",
        },
    },
    testnet: false,
});
export const storyAeneid = defineChain({
    id: 1315,
    name: "Story Aeneid",
    nativeCurrency: { name: "IP", symbol: "IP", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://story-aeneid.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://story-aeneid.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://story-aeneid.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://aeneid.storyscan.io",
        },
    },
    testnet: true,
});
export const celoMainnet = defineChain({
    id: 42220,
    name: "Celo Mainnet",
    nativeCurrency: { name: "Celo native asset", symbol: "CELO", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://celo-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://celo-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://celo-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://celo.blockscout.com/",
        },
    },
    testnet: false,
});
export const celoSepolia = defineChain({
    id: 11142220,
    name: "Celo Sepolia",
    nativeCurrency: { name: "Celo native asset", symbol: "CELO", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://celo-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://celo-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://celo-sepolia.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://celo-sepolia.blockscout.com/",
        },
    },
    testnet: true,
});
export const teaSepolia = defineChain({
    id: 10218,
    name: "Tea Sepolia",
    nativeCurrency: { name: "TEA", symbol: "TEA", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://tea-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://tea-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://tea-sepolia.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://sepolia.tea.xyz/",
        },
    },
    testnet: true,
});
export const bobaSepolia = defineChain({
    id: 28882,
    name: "Boba Sepolia",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://boba-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://boba-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://boba-sepolia.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://sepolia.testnet.bobascan.com/",
        },
    },
    testnet: true,
});
export const bobaMainnet = defineChain({
    id: 288,
    name: "Boba Mainnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://boba-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://boba-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://boba-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://bobascan.com/",
        },
    },
    testnet: false,
});
export const edgeMainnet = defineChain({
    id: 3343,
    name: "Edge Mainnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://edge-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://edge-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://edge-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://edge-mainnet.blockscout.com/",
        },
    },
    testnet: false,
});
export const worldl3devnet = defineChain({
    id: 48011,
    name: "WorldL3 Devnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://worldl3-devnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://worldl3-devnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://worldl3-devnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://worldl3-devnet.blockscout.com/",
        },
    },
    testnet: true,
});
export const stableMainnet = defineChain({
    id: 988,
    name: "Stable Mainnet",
    nativeCurrency: { name: "gUSDT", symbol: "gUSDT", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://stable-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://stable-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://stable-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://stablescan.xyz/",
        },
    },
    testnet: false,
});
export const mythosMainnet = defineChain({
    id: 42018,
    name: "Mythos Mainnet",
    nativeCurrency: { name: "eth", symbol: "eth", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://mythos-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://mythos-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://mythos-mainnet.g.alchemy.com/v2"],
        },
    },
    blockExplorers: {
        default: {
            name: "Block Explorer",
            url: "https://mythos-mainnet.explorer.alchemy.com/",
        },
    },
    testnet: false,
});
//# sourceMappingURL=chains.js.map